import React, { useEffect, useMemo, useRef, useState } from 'react'

import * as pdfjsLib from 'pdfjs-dist/build/pdf'
import PropTypes from 'prop-types'

import { findMatchingEntity, isDocumentBoilerplateText } from './entityMatcher'
import { computeImageHighlights, hasSubstantiveToken } from './ImageHighlightViewer'

// Configure local worker to avoid any external network requests or CDN dependencies
if (typeof window !== 'undefined') {
  pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`
}

/**
 * Normalizes text for fuzzy snippet matching on PDF text items
 */
function normalizeText(str = '') {
  return (str || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')
}

/**
 * Sorts PDF text items into logical, column-aware reading order using Recursive XY-Cut.
 *
 * Standard PDF streams often emit text in arbitrary operator order or row-interleaved order.
 * This causes multi-column documents (like workers' comp forms) to interleave cells from different
 * columns into the same DOM sequence. By recursively segmenting along major horizontal breaks
 * and vertical column gutters, items within each column or box remain contiguous in the DOM tree,
 * preventing cross-column text selection jumping.
 */
export function sortTextItemsByLayout(rawItems) {
  if (!rawItems || rawItems.length <= 1) return rawItems
  if (rawItems.some(it => it.str === undefined)) return rawItems

  const boxes = rawItems.map((it, idx) => {
    const x = it.transform ? it.transform[4] : 0
    const y = it.transform ? it.transform[5] : 0
    const w = it.width || 4
    const h = Math.abs(it.transform?.[3]) || it.height || 10
    return {
      it,
      origIdx: idx,
      x1: x,
      x2: x + w,
      y1: y,
      y2: y + h,
    }
  })

  function partition(boxList, depth = 0) {
    if (boxList.length <= 1 || depth > 25) return boxList

    // 1. Major Horizontal Section Break (gap >= 15pt)
    const sortedY = [...boxList].sort((a, b) => b.y2 - a.y2)
    let bestYCut = -1
    let maxYGap = 0
    let curBottom = sortedY[0].y1
    for (let i = 1; i < sortedY.length; i++) {
      const gap = curBottom - sortedY[i].y2
      if (gap >= 15 && gap > maxYGap) {
        maxYGap = gap
        bestYCut = i
      }
      curBottom = Math.min(curBottom, sortedY[i].y1)
    }

    if (bestYCut > 0 && maxYGap >= 15) {
      const topGroup = sortedY.slice(0, bestYCut)
      const bottomGroup = sortedY.slice(bestYCut)
      return [...partition(topGroup, depth + 1), ...partition(bottomGroup, depth + 1)]
    }

    // 2. Vertical Column Gutter (gap >= 10pt)
    const sortedX = [...boxList].sort((a, b) => a.x1 - b.x1)
    let bestXCut = -1
    let maxXGap = 0
    let curRight = sortedX[0].x2
    for (let i = 1; i < sortedX.length; i++) {
      const gap = sortedX[i].x1 - curRight
      if (gap >= 10 && gap > maxXGap) {
        maxXGap = gap
        bestXCut = i
      }
      curRight = Math.max(curRight, sortedX[i].x2)
    }

    if (bestXCut > 0 && maxXGap >= 10) {
      const leftGroup = sortedX.slice(0, bestXCut)
      const rightGroup = sortedX.slice(bestXCut)
      return [...partition(leftGroup, depth + 1), ...partition(rightGroup, depth + 1)]
    }

    // 3. Minor Horizontal Row Gap within a block (gap >= 4pt)
    if (bestYCut > 0 && maxYGap >= 4) {
      const topGroup = sortedY.slice(0, bestYCut)
      const bottomGroup = sortedY.slice(bestYCut)
      return [...partition(topGroup, depth + 1), ...partition(bottomGroup, depth + 1)]
    }

    // 4. Base Case: Reading order (top-to-bottom, left-to-right)
    return [...boxList].sort((a, b) => {
      const dy = b.y1 - a.y1
      if (Math.abs(dy) > 3) {
        return dy > 0 ? 1 : -1
      }
      return a.x1 - b.x1
    })
  }

  const sortedBoxes = partition(boxes)
  return sortedBoxes.map(b => b.it)
}

/**
 * Self-contained PageCanvas component that renders a single PDF page.
 *
 * Prevents multiple concurrent render operations on the same canvas,
 * cancels previous in-flight tasks cleanly, and resets the 2D transformation
 * matrix to identity to prevent pages from rendering backwards or upside down.
 */
const PageCanvas = React.memo(function PageCanvas({ pdfDoc, pageNum, scale, baseViewport }) {
  const canvasRef = useRef(null)
  const renderTaskRef = useRef(null)

  useEffect(() => {
    if (!pdfDoc || !canvasRef.current) return

    let isCancelled = false

    // 1. Cancel previous render task if one is already running on this canvas
    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel()
      } catch {}
      renderTaskRef.current = null
    }

    pdfDoc
      .getPage(pageNum)
      .then(page => {
        if (isCancelled) return

        const canvas = canvasRef.current
        if (!canvas) return

        const dpr = window.devicePixelRatio || 1
        const viewport = page.getViewport({ scale: scale * dpr })

        const ctx = canvas.getContext('2d')

        // 2. CRITICAL: Always reset the 2D transformation matrix to identity before sizing or rendering!
        // This prevents PDF coordinate transforms from compounding and rendering backwards/upside-down!
        ctx.setTransform(1, 0, 0, 1, 0, 0)
        ctx.clearRect(0, 0, canvas.width, canvas.height)

        canvas.width = viewport.width
        canvas.height = viewport.height
        canvas.style.width = `${viewport.width / dpr}px`
        canvas.style.height = `${viewport.height / dpr}px`

        const renderContext = {
          canvasContext: ctx,
          viewport,
        }

        const renderTask = page.render(renderContext)
        renderTaskRef.current = renderTask

        return renderTask.promise
      })
      .then(() => {
        renderTaskRef.current = null
      })
      .catch(err => {
        // RenderingCancelledException is expected and safe when scale/page changes rapidly
        if (err?.name === 'RenderingCancelledException' || err?.message?.includes('cancelled')) {
          return
        }
        // eslint-disable-next-line no-console
        console.warn(`Render error on page ${pageNum}:`, err)
      })

    return () => {
      isCancelled = true
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel()
        } catch {}
        renderTaskRef.current = null
      }
    }
  }, [pdfDoc, pageNum, scale])

  // Approximate default dimensions before render completes to prevent layout shift
  const fallbackWidth = baseViewport ? Math.round(baseViewport.width * scale) : Math.round(612 * scale)
  const fallbackHeight = baseViewport ? Math.round(baseViewport.height * scale) : Math.round(792 * scale)

  return (
    <canvas
      ref={canvasRef}
      className="pdf-page-canvas d-block"
      style={{
        width: `${fallbackWidth}px`,
        height: `${fallbackHeight}px`,
        display: 'block',
      }}
    />
  )
})

PageCanvas.propTypes = {
  pdfDoc: PropTypes.object,
  pageNum: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,
  baseViewport: PropTypes.object,
}

/**
 * Scans rendered textLayer spans and tags document boilerplate (official form titles,
 * state banners, statutory citations, and multi-line wrapped title fragments) with
 * the CSS class 'hitl-boilerplate-span'.
 * This suppresses the clickable hover styling and pointer cursor across both single-line
 * and word-wrapped multi-line title banners regardless of where line breaks occur.
 */
export function markBoilerplateSpans(container) {
  if (!container) return
  const spans = Array.from(container.querySelectorAll('span'))
  if (spans.length === 0) return

  // Calculate container page dimensions and font metrics for universal layout classification
  const containerHeight =
    parseFloat(container.style?.height || 0) ||
    container.clientHeight ||
    (container.getBoundingClientRect ? container.getBoundingClientRect().height : 0) ||
    792

  const headerZoneThreshold = containerHeight * 0.12
  const footerZoneThreshold = containerHeight * 0.94

  // Collect font sizes of text spans to determine median body font size
  const fontSizes = []
  spans.forEach(s => {
    const fs = parseFloat(s.style?.fontSize || 0) || s.offsetHeight || 0
    if (fs > 4) fontSizes.push(fs)
  })
  fontSizes.sort((a, b) => a - b)
  const medianFontSize = fontSizes.length > 0 ? fontSizes[Math.floor(fontSizes.length / 2)] : 10

  // Pass 1: Individual span matching (Structural Layout + Typography + Content Fallback)
  spans.forEach(s => {
    const text = (s.textContent || '').trim()
    if (!text) return

    const top = parseFloat(s.style?.top || 0)
    const fs = parseFloat(s.style?.fontSize || 0) || s.offsetHeight || 0

    // A. Structural Display Heading in Header Zone (Universal title detection for any form)
    const isHeaderZone = top > 0 && top < headerZoneThreshold
    const isDisplayHeading = fs >= 13 || (medianFontSize > 0 && fs >= medianFontSize * 1.25)
    if (isHeaderZone && isDisplayHeading && !isFieldLabelLike(text)) {
      s.classList.add('hitl-boilerplate-span')
      return
    }

    // B. Structural Footer / Margin Chrome (Page numbers, revision dates, statutory fine print)
    const isFooterZone = top > footerZoneThreshold
    if (isFooterZone && !isFieldLabelLike(text)) {
      s.classList.add('hitl-boilerplate-span')
      return
    }

    // C. Content Fallback (Universal keywords, citations, banners)
    if (isDocumentBoilerplateText(text)) {
      s.classList.add('hitl-boilerplate-span')
    }
  })

  // Pass 2: Multi-line wrapped title stitching for spans that are not already tagged
  // Look at consecutive spans in close proximity (e.g. multi-line header banners)
  for (let i = 0; i < spans.length; i++) {
    const s1 = spans[i]
    if (s1.classList.contains('hitl-boilerplate-span')) continue
    const t1 = (s1.textContent || '').trim()
    if (!t1 || isFieldLabelLike(t1)) continue
    let accumulated = t1
    for (let j = i + 1; j < Math.min(spans.length, i + 6); j++) {
      const s2 = spans[j]
      const t2 = (s2.textContent || '').trim()
      if (!t2 || isFieldLabelLike(t2)) break
      const combined = `${accumulated} ${t2}`
      if (isDocumentBoilerplateText(combined)) {
        for (let k = i; k <= j; k++) {
          spans[k].classList.add('hitl-boilerplate-span')
        }
        break
      }
      accumulated = combined
    }
  }
}

const PageTextLayer = React.memo(function PageTextLayer({ pdfDoc, pageNum, pageData, scale, onTextClick }) {
  const containerRef = useRef(null)
  const renderTaskRef = useRef(null)

  useEffect(() => {
    if (!pdfDoc || !containerRef.current) return
    let isCancelled = false

    if (renderTaskRef.current) {
      try {
        renderTaskRef.current.cancel()
      } catch {}
      renderTaskRef.current = null
    }

    const container = containerRef.current
    container.innerHTML = ''

    pdfDoc
      .getPage(pageNum)
      .then(async page => {
        if (isCancelled || !containerRef.current) return
        const viewport = page.getViewport({ scale })

        container.style.width = `${Math.round(viewport.width)}px`
        container.style.height = `${Math.round(viewport.height)}px`

        const rawContent = pageData?.textContent || (await page.getTextContent())
        if (isCancelled || !containerRef.current) return
        container.innerHTML = ''

        // Ensure text items are in column-aware reading order so multi-line text stays contiguous in the DOM
        const textContent = {
          ...rawContent,
          items: sortTextItemsByLayout(rawContent.items),
        }

        if (typeof pdfjsLib.renderTextLayer === 'function') {
          const task = pdfjsLib.renderTextLayer({
            textContent,
            container,
            viewport,
            textDivs: [],
          })
          renderTaskRef.current = task
          return task.promise.then(() => {
            markBoilerplateSpans(container)
          })
        } else if (textContent?.items) {
          // Fallback manual rendering for test environments where renderTextLayer is mocked
          textContent.items.forEach(item => {
            if (!item.str || !item.str.trim()) return
            const fontHeight = Math.abs(item.transform?.[3] || item.height || 10)
            const left = Math.round((item.transform?.[4] || 0) * scale)
            const top = Math.round((viewport.height - (item.transform?.[5] || 0) - fontHeight) * scale)
            const width = Math.round((item.width || fontHeight * 0.6) * scale)
            const fontSize = Math.round(fontHeight * scale)

            const span = document.createElement('span')
            span.style.left = `${Math.max(0, left)}px`
            span.style.top = `${Math.max(0, top)}px`
            span.style.width = `${Math.max(4, width)}px`
            span.style.height = `${Math.max(8, fontSize)}px`
            span.style.fontSize = `${fontSize}px`
            span.textContent = item.str
            container.appendChild(span)
          })
          markBoilerplateSpans(container)
        }
      })
      .catch(err => {
        if (err?.name !== 'RenderingCancelledException') {
          // eslint-disable-next-line no-console
          console.warn(`TextLayer error on page ${pageNum}:`, err)
        }
      })

    return () => {
      isCancelled = true
      if (renderTaskRef.current) {
        try {
          renderTaskRef.current.cancel()
        } catch {}
        renderTaskRef.current = null
      }
    }
  }, [pdfDoc, pageNum, pageData, scale])

  return (
    /* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */
    <div
      ref={containerRef}
      className="textLayer pdf-text-layer"
      onClick={e => onTextClick && onTextClick(e, pageNum)}
    />
  )
})

PageTextLayer.propTypes = {
  pdfDoc: PropTypes.object,
  pageNum: PropTypes.number.isRequired,
  pageData: PropTypes.object,
  scale: PropTypes.number.isRequired,
  onTextClick: PropTypes.func,
}

export const getLevenshteinDistance = (a, b) => {
  if (a === b) return 0
  if (!a) return b.length
  if (!b) return a.length
  if (Math.abs(a.length - b.length) > 1) return 99
  const m = a.length
  const n = b.length
  const d = []
  for (let i = 0; i <= m; i++) d[i] = [i]
  for (let j = 0; j <= n; j++) d[0][j] = j
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
    }
  }
  return d[m][n]
}

export const isSubsequenceFuzzyMatch = (phraseStr, textStr) => {
  if (!phraseStr || !textStr) return false
  if (textStr === phraseStr || (phraseStr.length >= 6 && textStr.includes(phraseStr))) return true

  // Critical guard: Do not fuzzy-match if one string has 'treatment' and the other has 'injury'/'accident'
  const pHasTreatment = /\b(?:treatment|treated)\b/i.test(phraseStr)
  const tHasTreatment = /\b(?:treatment|treated)\b/i.test(textStr)
  const pHasInjury = /\b(?:injury|accident|illness|loss)\b/i.test(phraseStr)
  const tHasInjury = /\b(?:injury|accident|illness|loss)\b/i.test(textStr)
  if ((pHasTreatment && !tHasTreatment && tHasInjury) || (pHasInjury && !tHasInjury && tHasTreatment)) {
    return false
  }

  const pWords = phraseStr.split(' ').filter(w => w.length >= 2)
  const tWords = textStr.split(' ').filter(w => w.length >= 2)
  if (pWords.length < 2 || tWords.length < pWords.length) return false
  for (let offset = 0; offset <= tWords.length - pWords.length; offset++) {
    let matches = 0
    for (let k = 0; k < pWords.length; k++) {
      const pw = pWords[k]
      const tw = tWords[offset + k]
      if (pw === tw) {
        matches++
      } else if (pw.length >= 4 && tw.length >= 4 && getLevenshteinDistance(pw, tw) <= 1) {
        matches++
      }
    }
    const minRatio = pWords.length <= 3 ? 1.0 : 0.75
    if (matches / pWords.length >= minRatio) return true
  }
  return false
}

export function computeActiveHighlights({
  pageDataMap = {},
  lineBlocks = [],
  firstPageViewport = null,
  scale = 1.3,
  activeSnippet = '',
  activeEntityValue = '',
  activeCategory = '',
  activeEntityField = '',
  activePage = 1,
  activeLineNumber = null,
  numPages = 0,
}) {
  const highlights = {}
  if (!activeSnippet && !activeEntityValue) return highlights

  const cleanSnippet = normalizeText(activeSnippet)
  const cleanEntity = normalizeText(activeEntityValue)

  // Helper: merge contiguous matching items on the same visual line into a single clean bounding box
  const mergeItemsIntoBoxes = (matchedItems, label) => {
    if (!matchedItems || matchedItems.length === 0) return []
    const lineGroups = []
    matchedItems.forEach(it => {
      let line = lineGroups.find(lg => Math.abs(lg.top - it.top) < 8)
      if (!line) {
        line = {
          left: it.left,
          top: it.top,
          right: it.right,
          bottom: it.bottom,
          items: [it],
        }
        lineGroups.push(line)
      } else {
        line.left = Math.min(line.left, it.left)
        line.top = Math.min(line.top, it.top)
        line.right = Math.max(line.right, it.right)
        line.bottom = Math.max(line.bottom, it.bottom)
        line.items.push(it)
      }
    })

    return lineGroups.map((lg, idx) => ({
      left: Math.max(0, Math.round(lg.left - 2)),
      top: Math.max(0, Math.round(lg.top - 2)),
      width: Math.max(8, Math.round(lg.right - lg.left + 4)),
      height: Math.max(10, Math.round(lg.bottom - lg.top + 4)),
      str: lg.items.map(i => i.str).join(' '),
      label: label || lg.items.map(i => i.str).join(' '),
      priority: 1,
      idx,
    }))
  }

  const isGenericLabelSnippet =
    cleanSnippet === 'date of birth' ||
    cleanSnippet === 'birthdate' ||
    cleanSnippet === 'dob' ||
    cleanSnippet === 'date of injury' ||
    cleanSnippet === 'injury date' ||
    cleanSnippet === 'date of injury illness' ||
    cleanSnippet === 'injury illness date' ||
    cleanSnippet === 'date employer notified' ||
    cleanSnippet === 'employer notified' ||
    cleanSnippet === 'claim number' ||
    cleanSnippet === 'policy number'

  const isAppointmentField =
    (activeCategory || '').toLowerCase() === 'appointment' ||
    (activeEntityField || '').toLowerCase().includes('appointment') ||
    (activeEntityField || '').toLowerCase().includes('referral') ||
    (activeEntityField || '').toLowerCase().includes('visit') ||
    (activeEntityField || '').toLowerCase().includes('evaluation') ||
    (activeEntityField || '').toLowerCase().includes('treatment')

  const effectiveCleanSnippet = isAppointmentField
    ? cleanSnippet.replace(/;?\s*(?:date|time)\s+of\s+injury\s*[:-]?\s*[^;]*/gi, '').trim()
    : cleanSnippet

  const isPureDateField =
    ((activeEntityField || '').toLowerCase().includes('date') ||
      (activeEntityField || '').toLowerCase().includes('birth')) &&
    !isAppointmentField

  const isDateField =
    isPureDateField ||
    (!isAppointmentField &&
      (/\b\d{1,2}[\s/-]\d{1,2}[\s/-]\d{2,4}\b/.test(activeEntityValue || '') ||
        /\b\d{4}[\s/-]\d{1,2}[\s/-]\d{1,2}\b/.test(activeEntityValue || '')))

  const isInjuryDateField = (activeEntityField || '').toLowerCase().includes('injury') && isDateField
  const isAddressField = (activeEntityField || '').toLowerCase().includes('address')

  const isInjuryOrAccidentField =
    (activeEntityField || '').toLowerCase().includes('injury') ||
    (activeEntityField || '').toLowerCase().includes('accident')

  const isCarrierField =
    (activeEntityField || '').toLowerCase().includes('carrier') ||
    (activeEntityField || '').toLowerCase().includes('insurer') ||
    (activeEntityField || '').toLowerCase().includes('insurance')

  const isProviderField =
    (activeEntityField || '').toLowerCase().includes('provider') ||
    (activeEntityField || '').toLowerCase().includes('facility') ||
    (activeEntityField || '').toLowerCase().includes('hospital') ||
    (activeEntityField || '').toLowerCase().includes('physician') ||
    (activeEntityField || '').toLowerCase().includes('treating')

  const searchPage = pageNum => {
    const pageData = pageDataMap[pageNum]
    if (!pageData?.items || !pageData?.baseViewport) return []

    const displayViewport = pageData.baseViewport.clone({ scale })

    // Convert raw text items to display coordinates
    const itemsWithCoords = pageData.items.map(item => {
      const rect = [
        item.transform[4],
        item.transform[5],
        item.transform[4] + item.width,
        item.transform[5] + item.height,
      ]
      const viewRect = displayViewport.convertToViewportRectangle(rect)
      return {
        ...item,
        left: Math.min(viewRect[0], viewRect[2]),
        top: Math.min(viewRect[1], viewRect[3]),
        right: Math.max(viewRect[0], viewRect[2]),
        bottom: Math.max(viewRect[1], viewRect[3]),
        width: Math.max(Math.abs(viewRect[2] - viewRect[0]), 4),
        height: Math.max(Math.abs(viewRect[3] - viewRect[1]), 8),
      }
    })

    const getAssociatedContext = item => {
      if (!item) return ''
      // 1. Column header vertically above in the same column (dx < 70, dy between 5 and 90)
      const columnHeaders = itemsWithCoords.filter(other => {
        if (other === item) return false
        const dx = Math.abs((other.left || 0) - (item.left || 0))
        const dy = (item.top || 0) - (other.top || 0)
        return dx < 70 && dy > 5 && dy < 90 && other.cleanStr
      })
      // 2. Inline label directly on the left (dy < 15, dx between 5 and 250)
      const inlineHeaders = itemsWithCoords.filter(other => {
        if (other === item) return false
        const dy = Math.abs((other.top || 0) - (item.top || 0))
        const dx = (item.left || 0) - (other.left || 0)
        return dy < 15 && dx > 0 && dx < 250 && other.cleanStr
      })
      const pdfContext = [...columnHeaders, ...inlineHeaders].map(h => h.cleanStr).join(' ')

      let ocrContext = ''
      if (lineBlocks && lineBlocks.length > 0 && displayViewport) {
        const normLeft = (item.left || 0) / (displayViewport.width || 612)
        const normTop = (item.top || 0) / (displayViewport.height || 792)
        const ocrHeaders = lineBlocks.filter(b => {
          if (b.page && b.page !== pageNum) return false
          const bLeft = b.left || 0
          const bTop = b.top || 0
          const dxCol = Math.abs(bLeft - normLeft)
          const dyCol = normTop - bTop
          const isColHeader = dxCol < 0.12 && dyCol > 0.005 && dyCol < 0.15
          const dyInline = Math.abs(normTop - bTop)
          const dxInline = normLeft - bLeft
          const isInlineLabel = dyInline < 0.02 && dxInline > 0.01 && dxInline < 0.35
          return isColHeader || isInlineLabel
        })
        ocrContext = ocrHeaders.map(b => (b.text || '').toLowerCase()).join(' ')
      }
      return `${pdfContext} ${ocrContext}`.trim()
    }

    let foundBoxes = []

    // A. Special Multi-Part Handling for Address fields spanning separate lines
    if (isAddressField) {
      const addrSource = activeEntityValue || activeSnippet || ''
      const parts = addrSource
        .split(/[,;\n]+/)
        .map(p => normalizeText(p))
        .filter(p => p.length >= 3)

      if (parts.length >= 1) {
        // Look for the street address (parts[0]) as anchor
        const streetPart = parts[0]
        let streetMatch = null

        for (let i = 0; i < itemsWithCoords.length; i++) {
          let accumulated = ''
          const matchedItems = []
          for (let j = i; j < Math.min(i + 15, itemsWithCoords.length); j++) {
            const item = itemsWithCoords[j]
            if (!item.cleanStr) continue
            accumulated = (accumulated + ' ' + item.cleanStr).trim()
            matchedItems.push(item)

            const matches =
              accumulated === streetPart ||
              (streetPart.length >= 6 && accumulated.includes(streetPart)) ||
              (streetPart.length >= 10 && isSubsequenceFuzzyMatch(streetPart, accumulated))

            if (matches) {
              // Trim leading/trailing non-matching items from the street match
              let highlightItems = [...matchedItems]
              while (highlightItems.length > 1) {
                const withoutFirst = highlightItems.slice(1)
                const accWithoutFirst = withoutFirst
                  .map(it => it.cleanStr)
                  .join(' ')
                  .trim()
                if (
                  accWithoutFirst === streetPart ||
                  (streetPart.length >= 6 && accWithoutFirst.includes(streetPart)) ||
                  (streetPart.length >= 10 && isSubsequenceFuzzyMatch(streetPart, accWithoutFirst))
                ) {
                  highlightItems = withoutFirst
                } else {
                  break
                }
              }
              while (highlightItems.length > 1) {
                const withoutLast = highlightItems.slice(0, -1)
                const accWithoutLast = withoutLast
                  .map(it => it.cleanStr)
                  .join(' ')
                  .trim()
                if (
                  accWithoutLast === streetPart ||
                  (streetPart.length >= 6 && accWithoutLast.includes(streetPart)) ||
                  (streetPart.length >= 10 && isSubsequenceFuzzyMatch(streetPart, accWithoutLast))
                ) {
                  highlightItems = withoutLast
                } else {
                  break
                }
              }
              streetMatch = {
                items: highlightItems,
                endIndex: j,
              }
              break
            }
            if (accumulated.length > streetPart.length + 30) break
          }
          if (streetMatch) break
        }

        if (streetMatch) {
          const allAddressItems = [...streetMatch.items]

          // Search for subsequent parts (city, state, zip) in adjacent items immediately following the street match
          if (parts.length > 1) {
            const searchWindowStart = streetMatch.endIndex + 1
            const searchWindowEnd = Math.min(searchWindowStart + 25, itemsWithCoords.length)
            const matchedPartIndices = new Set()

            for (let pIdx = 1; pIdx < parts.length; pIdx++) {
              const part = parts[pIdx]
              for (let i = searchWindowStart; i < searchWindowEnd; i++) {
                if (matchedPartIndices.has(i)) continue
                let accumulated = ''
                const subMatched = []
                for (let j = i; j < Math.min(i + 10, searchWindowEnd); j++) {
                  const item = itemsWithCoords[j]
                  if (!item.cleanStr) continue
                  accumulated = (accumulated + ' ' + item.cleanStr).trim()
                  subMatched.push(item)

                  const matches =
                    accumulated === part ||
                    (part.length >= 6 && accumulated.includes(part)) ||
                    (part.length >= 8 && isSubsequenceFuzzyMatch(part, accumulated))

                  if (matches) {
                    let trimmed = [...subMatched]
                    while (trimmed.length > 1) {
                      const withoutFirst = trimmed.slice(1)
                      const accWithoutFirst = withoutFirst
                        .map(it => it.cleanStr)
                        .join(' ')
                        .trim()
                      if (
                        accWithoutFirst === part ||
                        (part.length >= 6 && accWithoutFirst.includes(part)) ||
                        (part.length >= 8 && isSubsequenceFuzzyMatch(part, accWithoutFirst))
                      ) {
                        trimmed = withoutFirst
                      } else {
                        break
                      }
                    }
                    while (trimmed.length > 1) {
                      const withoutLast = trimmed.slice(0, -1)
                      const accWithoutLast = withoutLast
                        .map(it => it.cleanStr)
                        .join(' ')
                        .trim()
                      if (
                        accWithoutLast === part ||
                        (part.length >= 6 && accWithoutLast.includes(part)) ||
                        (part.length >= 8 && isSubsequenceFuzzyMatch(part, accWithoutLast))
                      ) {
                        trimmed = withoutLast
                      } else {
                        break
                      }
                    }
                    trimmed.forEach(it => {
                      const origIdx = itemsWithCoords.indexOf(it)
                      if (origIdx >= 0) matchedPartIndices.add(origIdx)
                    })
                    allAddressItems.push(...trimmed)
                    break
                  }
                  if (accumulated.length > part.length + 20) break
                }
                if (matchedPartIndices.size > 0 && subMatched.length > 0 && matchedPartIndices.has(i)) {
                  break
                }
              }
            }
          }

          foundBoxes = mergeItemsIntoBoxes(allAddressItems, addrSource)
        }
      }
    }

    // B. Build ordered candidate search phrases
    if (foundBoxes.length === 0) {
      const candidatePhrases = []

      // Appointment & Treatment candidates - prioritized at top of candidate search
      if (isAppointmentField) {
        const isEmergency =
          cleanEntity.includes('emergency') ||
          cleanSnippet.includes('emergency') ||
          (activeEntityField || '').toLowerCase().includes('emergency')

        if (isEmergency) {
          candidatePhrases.push(
            'treatment type emergency evaluation diagnostic testing and medical procedures',
            'treatment type emergency evaluation',
            'treatment type emergency room',
            'treatment type emergency',
            'initial treatment emergency room',
            'initial treatment emergency',
            'emergency evaluation diagnostic testing and medical procedures',
            'emergency evaluation',
            'emergency room',
            'treatment type',
            'type of treatment',
            'initial treatment',
            'emergency',
          )
        }

        const dateMatch = (activeEntityValue || '').match(
          /\b(\d{1,2}\s*[/.-]\s*\d{1,2}(?:\s*[/.-]\s*\d{2,4})?|\d{4}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{1,2})\b/,
        )
        if (dateMatch) {
          const cleanD = normalizeText(dateMatch[1])
          candidatePhrases.push(
            `date of treatment ${cleanD}`,
            `treatment date ${cleanD}`,
            `date of first treatment ${cleanD}`,
            `initial treatment ${cleanD}`,
            `treatment type ${cleanD}`,
          )
        }

        candidatePhrases.push(
          'date of treatment',
          'treatment date',
          'date of first treatment',
          'initial treatment',
          'treatment type',
          'type of treatment',
          'treating physician',
          'treating provider',
          'office visit',
          'follow up',
        )

        if (activeEntityValue && (activeEntityValue.includes('with ') || activeEntityValue.includes('at '))) {
          const provPart = normalizeText(activeEntityValue.replace(/.*(?:with|at)\s+/i, ''))
          if (provPart && provPart.length >= 4 && !candidatePhrases.includes(provPart)) {
            candidatePhrases.push(provPart)
          }
        }
      }

      // Date fields: prioritize date value
      if (isDateField && cleanEntity && cleanEntity.length >= 4) {
        const nonPadded = cleanEntity.replace(/\b0(\d)\b/g, '$1')
        const twoDigitYear = cleanEntity.replace(/\b(19|20)(\d{2})\b/g, '$2')
        const isoMatch = cleanEntity.match(/^(\d{4})\s+(\d{1,2})\s+(\d{1,2})$/)
        const usFromIso = isoMatch
          ? `${isoMatch[2].padStart(2, '0')} ${isoMatch[3].padStart(2, '0')} ${isoMatch[1]}`
          : ''
        const usFromIsoNonPadded = isoMatch
          ? `${parseInt(isoMatch[2], 10)} ${parseInt(isoMatch[3], 10)} ${isoMatch[1]}`
          : ''

        if (isInjuryDateField) {
          candidatePhrases.push(`injury illness date ${cleanEntity}`)
          candidatePhrases.push(`date of injury illness ${cleanEntity}`)
          candidatePhrases.push(`date of injury ${cleanEntity}`)
          candidatePhrases.push(`injury date ${cleanEntity}`)
          candidatePhrases.push(`date of accident ${cleanEntity}`)
          candidatePhrases.push(`accident date ${cleanEntity}`)
          if (nonPadded !== cleanEntity) {
            candidatePhrases.push(`injury illness date ${nonPadded}`)
            candidatePhrases.push(`date of injury illness ${nonPadded}`)
            candidatePhrases.push(`date of injury ${nonPadded}`)
            candidatePhrases.push(`injury date ${nonPadded}`)
          }
          if (usFromIso) {
            candidatePhrases.push(`date of injury ${usFromIso}`)
            candidatePhrases.push(`injury date ${usFromIso}`)
          }
          if (usFromIsoNonPadded && usFromIsoNonPadded !== usFromIso) {
            candidatePhrases.push(`date of injury ${usFromIsoNonPadded}`)
            candidatePhrases.push(`injury date ${usFromIsoNonPadded}`)
          }
        }

        candidatePhrases.push(cleanEntity)
        if (nonPadded !== cleanEntity) candidatePhrases.push(nonPadded)
        if (twoDigitYear !== cleanEntity) candidatePhrases.push(twoDigitYear)
        if (usFromIso && !candidatePhrases.includes(usFromIso)) candidatePhrases.push(usFromIso)
        if (usFromIsoNonPadded && !candidatePhrases.includes(usFromIsoNonPadded)) {
          candidatePhrases.push(usFromIsoNonPadded)
        }
      }

      // Add primary snippet if valid
      const targetCleanSnippet = isAppointmentField ? effectiveCleanSnippet : cleanSnippet
      if (targetCleanSnippet && targetCleanSnippet.length >= 3 && !isGenericLabelSnippet) {
        if (
          isInjuryDateField &&
          /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(targetCleanSnippet)
        ) {
          // Do not push competing employer notification snippet for injury date
        } else if (isAppointmentField) {
          const isInjurySnippet =
            /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date|injury\s+occurred)\b/i.test(
              targetCleanSnippet,
            )
          const isBareDigits =
            /^\s*(?:\d{1,2}\s*[/.-]\s*\d{1,2}(?:\s*[/.-]\s*\d{2,4})?|\d{4}\s*[/.-]\s*\d{1,2}\s*[/.-]\s*\d{1,2})\s*$/.test(
              targetCleanSnippet,
            )
          const hasTreatmentTerms =
            /\b(?:treatment|treated|emergency|clinic|hospital|physician|doctor|visit|evaluation|follow\s*up)\b/i.test(
              targetCleanSnippet,
            )
          if (
            !isInjurySnippet &&
            !isBareDigits &&
            hasTreatmentTerms &&
            !candidatePhrases.includes(targetCleanSnippet)
          ) {
            candidatePhrases.push(targetCleanSnippet)
          }
        } else if (isProviderField) {
          const entityTokens = cleanEntity
            .split(' ')
            .filter(
              t =>
                t.length >= 3 &&
                !/^(hospital|clinic|center|physician|provider|memorial|county|services|department|the|and|for|with)$/i.test(
                  t,
                ),
            )
          if (entityTokens.length === 0 || entityTokens.some(t => cleanSnippet.includes(t))) {
            candidatePhrases.push(cleanSnippet)
          }
        } else {
          candidatePhrases.push(cleanSnippet)
        }
      }

      // Add stripped snippet (removing common label prefixes like "detailed accident description:", "carrier:", etc.)
      if (cleanSnippet && !isGenericLabelSnippet && !isAppointmentField) {
        const isCompetingNotification =
          isInjuryDateField && /\b(?:notified|employer\s+notified|date\s+employer\s+notified)\b/i.test(cleanSnippet)

        if (!isCompetingNotification) {
          const strippedSnippet = cleanSnippet
            .replace(/^(?:detailed\s+)?accident\s+description\s*[:-]?\s*/i, '')
            .replace(/^(?:injury\s+)?description\s*[:-]?\s*/i, '')
            .replace(/^(?:insurance\s+)?carrier\s*[:-]?\s*/i, '')
            .replace(/^(?:treating\s+)?(?:physician|hospital|provider|facility)\s*(?:\/\s*hospital)?\s*[:-]?\s*/i, '')
            .trim()
          if (strippedSnippet && strippedSnippet.length >= 3 && !candidatePhrases.includes(strippedSnippet)) {
            if (!isProviderField) {
              candidatePhrases.push(strippedSnippet)
            } else {
              const entityTokens = cleanEntity
                .split(' ')
                .filter(
                  t =>
                    t.length >= 3 &&
                    !/^(hospital|clinic|center|physician|provider|memorial|county|services|department|the|and|for|with)$/i.test(
                      t,
                    ),
                )
              if (entityTokens.length === 0 || entityTokens.some(t => strippedSnippet.includes(t))) {
                candidatePhrases.push(strippedSnippet)
              }
            }
          }
          if (cleanSnippet.includes(':')) {
            const segments = cleanSnippet
              .split(/[;.\n]/)
              .map(s => s.trim())
              .filter(Boolean)
            const entityTokens = isProviderField
              ? cleanEntity
                  .split(' ')
                  .filter(
                    t =>
                      t.length >= 3 &&
                      !/^(hospital|clinic|center|physician|provider|memorial|county|services|department|the|and|for|with)$/i.test(
                        t,
                      ),
                  )
              : []
            for (const seg of segments) {
              if (seg.includes(':')) {
                const segVal = normalizeText(seg.split(':')[1])
                if (segVal && segVal.length >= 3 && !candidatePhrases.includes(segVal)) {
                  if (!isProviderField || entityTokens.length === 0 || entityTokens.some(t => segVal.includes(t))) {
                    candidatePhrases.push(segVal)
                  }
                }
              }
            }
          }
          if (cleanSnippet.includes('(')) {
            const withoutParens = cleanSnippet
              .replace(/\([^)]*\)/g, ' ')
              .replace(/\s+/g, ' ')
              .trim()
            if (withoutParens.length >= 3 && !candidatePhrases.includes(withoutParens)) {
              candidatePhrases.push(withoutParens)
            }
          }
        }
      }

      // Add entity value (avoid pushing bare appointment entity values containing dates)
      if (cleanEntity && cleanEntity.length >= 3 && !candidatePhrases.includes(cleanEntity)) {
        if (!isAppointmentField) {
          candidatePhrases.push(cleanEntity)
        }
      }

      if (activeEntityValue && activeEntityValue.includes('(') && !isAppointmentField) {
        const baseName = normalizeText(activeEntityValue.split('(')[0])
        if (baseName && baseName.length >= 3 && !candidatePhrases.includes(baseName)) {
          candidatePhrases.push(baseName)
        }
      }

      // Sub-phrases (5+ words) from cleanSnippet or cleanEntity for multi-line accident narratives
      if (!isAppointmentField) {
        const longSources = [cleanSnippet, cleanEntity].filter(s => s && !isGenericLabelSnippet)
        for (const src of longSources) {
          const words = src.split(' ').filter(w => w.length >= 2)
          if (words.length >= 6) {
            for (let start = 0; start <= words.length - 5; start += 3) {
              const sub = words.slice(start, start + 5).join(' ')
              if (isProviderField) {
                const entityTokens = cleanEntity
                  .split(' ')
                  .filter(
                    t =>
                      t.length >= 3 &&
                      !/^(hospital|clinic|center|physician|provider|memorial|county|services|department|the|and|for|with)$/i.test(
                        t,
                      ),
                  )
                if (entityTokens.length > 0 && !entityTokens.some(t => sub.includes(t))) {
                  continue
                }
              }
              if (!candidatePhrases.includes(sub)) {
                candidatePhrases.push(sub)
              }
            }
          }
        }
      }

      // 1. Injury Description candidates
      if (isInjuryOrAccidentField && !isDateField && !isAppointmentField) {
        const strippedEntity = cleanEntity
          .replace(/^(?:detailed\s+)?accident\s+description\s*[:-]?\s*/i, '')
          .replace(/^(?:injury\s+)?description\s*[:-]?\s*/i, '')
          .trim()
        if (strippedEntity && strippedEntity.length >= 4 && !candidatePhrases.includes(strippedEntity)) {
          candidatePhrases.push(strippedEntity)
        }

        const isDiagnosis = (activeEntityField || '').toLowerCase().includes('diagnosis')
        if (!isDiagnosis) {
          const injuryLabels = [
            'detailed accident description',
            'accident description',
            'describe how accident occurred',
            'injury description',
          ]
          injuryLabels.forEach(lbl => {
            if (!candidatePhrases.includes(lbl)) candidatePhrases.push(lbl)
          })
        }
      }

      // 2. Insurance Carrier candidates
      if (isCarrierField) {
        const strippedInsurer = cleanEntity
          .replace(/\b(?:insurance\s+)?company\b/i, '')
          .replace(/\b(?:insurance|company|co|corp|inc|llc)\b/gi, '')
          .trim()
        if (strippedInsurer && strippedInsurer.length >= 4 && !candidatePhrases.includes(strippedInsurer)) {
          candidatePhrases.push(strippedInsurer)
        }

        const carrierLabels = [
          `insurance carrier ${cleanEntity}`,
          `carrier ${cleanEntity}`,
          `insurance company ${cleanEntity}`,
          `insurance carrier ${strippedInsurer}`,
          `carrier ${strippedInsurer}`,
          'insurance carrier',
          'carrier name',
          'claims administrator',
        ]
        carrierLabels.forEach(lbl => {
          if (lbl && !candidatePhrases.includes(lbl)) candidatePhrases.push(lbl)
        })
      }

      // 3. Medical Provider candidates
      if (isProviderField) {
        const strippedHospital = cleanEntity
          .replace(/\b(?:county\s+)?(?:memorial\s+)?hospital\b/i, '')
          .replace(/\b(?:memorial|hospital|clinic|center|health|services)\b/gi, '')
          .trim()
        if (strippedHospital && strippedHospital.length >= 4 && !candidatePhrases.includes(strippedHospital)) {
          candidatePhrases.unshift(strippedHospital)
        }
        if (cleanEntity && cleanEntity.length >= 4 && !candidatePhrases.includes(cleanEntity)) {
          candidatePhrases.unshift(cleanEntity)
        }

        const providerLabels = [
          `treating physician hospital ${cleanEntity}`,
          `treating physician ${cleanEntity}`,
          `treating hospital ${cleanEntity}`,
          `medical provider ${cleanEntity}`,
          `treating provider ${cleanEntity}`,
          `treating physician hospital ${strippedHospital}`,
          `treating physician ${strippedHospital}`,
          `treating hospital ${strippedHospital}`,
        ]
        providerLabels.forEach(lbl => {
          if (lbl && !candidatePhrases.includes(lbl)) candidatePhrases.push(lbl)
        })
      }

      // Search candidate phrases
      for (const phrase of candidatePhrases) {
        if (foundBoxes.length > 0) break
        if (!phrase || phrase.length < 3) continue
        if (!hasSubstantiveToken(phrase)) continue

        // If appointment, NEVER search bare date digits
        if (
          isAppointmentField &&
          (/^\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?$/.test(phrase.trim()) ||
            /^\d{1,2}\s+\d{1,2}(?:\s+\d{2,4})?$/.test(phrase.trim()))
        ) {
          continue
        }

        const isPhraseExplicitInjury =
          phrase.includes('injury') || phrase.includes('accident') || phrase.includes('illness')

        const isPhraseDateDigitsOnly =
          /^\d{1,2}[/-]\d{1,2}(?:[/-]\d{2,4})?$/.test(phrase.trim()) ||
          /^\d{1,2}\s+\d{1,2}(?:\s+\d{2,4})?$/.test(phrase.trim())

        const targetLineBlock =
          activeLineNumber && lineBlocks?.length > 0
            ? lineBlocks.find(b => b.lineNumber === activeLineNumber && (b.page || 1) === pageNum)
            : null

        for (let i = 0; i < itemsWithCoords.length; i++) {
          if (isInjuryDateField) {
            const ctx = getAssociatedContext(itemsWithCoords[i])
            if (
              /\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(ctx) &&
              !/\b(?:injury|accident|illness)\b/i.test(ctx)
            ) {
              continue
            }
            if (/\b(?:treatment|treated)\b/i.test(ctx) && !/\b(?:injury|accident|illness)\b/i.test(ctx)) {
              continue
            }
          }

          if (isAppointmentField) {
            const contextAround = itemsWithCoords
              .slice(Math.max(0, i - 5), Math.min(itemsWithCoords.length, i + 6))
              .map(it => it.cleanStr)
              .join(' ')
            const hasInjuryContext =
              /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                contextAround,
              )
            const itemText = itemsWithCoords[i].cleanStr || ''
            const hasTreatmentKeywords =
              /\b(?:treatment|treated|emergency|clinic|hospital|physician|doctor|evaluation)\b/i.test(itemText)
            if (hasInjuryContext && !hasTreatmentKeywords) {
              continue
            }
          }

          if (!isInjuryDateField && !isPhraseExplicitInjury && isPhraseDateDigitsOnly) {
            const contextBefore = itemsWithCoords
              .slice(Math.max(0, i - 6), i)
              .map(it => it.cleanStr)
              .join(' ')
            if (
              /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                contextBefore,
              )
            ) {
              continue
            }
          }

          let accumulated = ''
          const matchedItems = []
          for (let j = i; j < Math.min(i + 80, itemsWithCoords.length); j++) {
            const item = itemsWithCoords[j]
            if (!item.cleanStr) continue
            accumulated = (accumulated + ' ' + item.cleanStr).trim()
            matchedItems.push(item)

            const matchesPhrase =
              accumulated === phrase ||
              (!isPhraseDateDigitsOnly && phrase.length >= 8 && accumulated.includes(phrase)) ||
              (isPhraseDateDigitsOnly && accumulated.length <= phrase.length + 8 && accumulated.includes(phrase)) ||
              (!isPhraseDateDigitsOnly && phrase.length >= 10 && isSubsequenceFuzzyMatch(phrase, accumulated))

            if (matchesPhrase) {
              if (isInjuryDateField) {
                const ctx = getAssociatedContext(itemsWithCoords[i])
                if (
                  /\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(ctx) &&
                  !/\b(?:injury|accident|illness)\b/i.test(ctx)
                ) {
                  break
                }
                if (/\b(?:treatment|treated)\b/i.test(ctx) && !/\b(?:injury|accident|illness)\b/i.test(ctx)) {
                  break
                }
              }

              if (targetLineBlock && isDateField && matchedItems.length > 0) {
                const normLeft = (matchedItems[0].left || 0) / (displayViewport.width || 612)
                const normTop = (matchedItems[0].top || 0) / (displayViewport.height || 792)
                const dist = Math.hypot(normLeft - targetLineBlock.left, normTop - targetLineBlock.top)
                if (dist > 0.25) {
                  break
                }
              }

              if (isAppointmentField) {
                const contextAroundMatch = itemsWithCoords
                  .slice(Math.max(0, i - 4), Math.min(itemsWithCoords.length, j + 5))
                  .map(it => it.cleanStr)
                  .join(' ')
                const hasInjuryContext =
                  /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                    contextAroundMatch,
                  )
                const hasTreatmentInAcc =
                  /\b(?:treatment|treated|emergency|clinic|hospital|physician|doctor|evaluation)\b/i.test(accumulated)
                if (
                  /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                    accumulated,
                  ) ||
                  (hasInjuryContext && !hasTreatmentInAcc)
                ) {
                  break
                }
              }

              if (!isInjuryDateField && !isPhraseExplicitInjury) {
                if (isPhraseDateDigitsOnly) {
                  const contextBefore = itemsWithCoords
                    .slice(Math.max(0, i - 6), i)
                    .map(it => it.cleanStr)
                    .join(' ')
                  if (
                    /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                      contextBefore,
                    )
                  ) {
                    break
                  }
                }
                if (
                  (isAppointmentField || isDateField) &&
                  /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date|date\s+of\s+loss|loss\s+date)\b/i.test(
                    accumulated,
                  )
                ) {
                  break
                }
              }

              // Trim leading/trailing excess items if remaining items still match phrase
              let highlightItems = [...matchedItems]
              while (highlightItems.length > 1) {
                const withoutFirst = highlightItems.slice(1)
                const accWithoutFirst = withoutFirst
                  .map(it => it.cleanStr)
                  .join(' ')
                  .trim()
                if (
                  accWithoutFirst === phrase ||
                  (phrase.length >= 8 && accWithoutFirst.includes(phrase)) ||
                  (!isPhraseDateDigitsOnly && phrase.length >= 10 && isSubsequenceFuzzyMatch(phrase, accWithoutFirst))
                ) {
                  highlightItems = withoutFirst
                } else {
                  break
                }
              }

              while (highlightItems.length > 1) {
                const withoutLast = highlightItems.slice(0, -1)
                const accWithoutLast = withoutLast
                  .map(it => it.cleanStr)
                  .join(' ')
                  .trim()
                if (
                  accWithoutLast === phrase ||
                  (phrase.length >= 8 && accWithoutLast.includes(phrase)) ||
                  (!isPhraseDateDigitsOnly && phrase.length >= 10 && isSubsequenceFuzzyMatch(phrase, accWithoutLast))
                ) {
                  highlightItems = withoutLast
                } else {
                  break
                }
              }

              // If labeled date phrase matched, strip label items
              if (isDateField && isPhraseExplicitInjury) {
                const dateItems = highlightItems.filter(
                  it => !/\b(?:date|of|injury|illness|accident)\b/i.test((it.cleanStr || '').trim()),
                )
                if (dateItems.length > 0) {
                  highlightItems = dateItems
                }
              }

              if (isInjuryDateField) {
                const hasNotificationContext = highlightItems.some(it => {
                  const ctx = getAssociatedContext(it)
                  return (
                    /\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(ctx) &&
                    !/\b(?:injury|accident|illness)\b/i.test(ctx)
                  )
                })
                if (hasNotificationContext) {
                  break
                }
              }

              foundBoxes = mergeItemsIntoBoxes(highlightItems, activeEntityValue || phrase)
              break
            }

            if (accumulated.length > Math.max(phrase.length + 80, phrase.length * 2)) {
              break
            }
          }
          if (foundBoxes.length > 0) break
        }
      }
    }

    // C. Fallback for distinct entity tokens
    if (foundBoxes.length === 0 && cleanEntity && cleanEntity.length >= 4) {
      const stopWords = new Set([
        'date',
        'name',
        'number',
        'claim',
        'injury',
        'report',
        'code',
        'status',
        'first',
        'birth',
        'work',
        'none',
        'pending',
      ])
      if (!stopWords.has(cleanEntity)) {
        const matchingItems = itemsWithCoords.filter((it, idx) => {
          if (!it.cleanStr) return false
          if (isInjuryDateField) {
            const ctx = getAssociatedContext(it)
            if (
              /\b(?:notified|employer\s+notified|date\s+employer\s+notified|date\s+employer)\b/i.test(ctx) &&
              !/\b(?:injury|accident|illness)\b/i.test(ctx)
            ) {
              return false
            }
            if (/\b(?:treatment|treated)\b/i.test(ctx) && !/\b(?:injury|accident|illness)\b/i.test(ctx)) {
              return false
            }
          }
          const words = it.cleanStr.split(' ')
          return it.cleanStr === cleanEntity || words.includes(cleanEntity)
        })
        if (matchingItems.length > 0) {
          let chosenItem = matchingItems[0]
          if (isInjuryDateField && matchingItems.length > 1) {
            const injuryContextItem = matchingItems.find(it => {
              const ctx = getAssociatedContext(it)
              return /\b(?:injury|accident|illness)\b/i.test(ctx)
            })
            if (injuryContextItem) chosenItem = injuryContextItem
          }
          foundBoxes = mergeItemsIntoBoxes([chosenItem], cleanEntity)
        }
      }
    }

    if (isAppointmentField && foundBoxes.length > 0) {
      const allText = foundBoxes.map(b => (b.str || '').toLowerCase()).join(' ')
      const hasTreatment =
        /\b(?:treatment|treated|emergency|clinic|hospital|physician|doctor|evaluation|visit|follow\s*up)\b/i.test(
          allText,
        )
      const hasInjuryOnly = /\b(?:date\s+of\s+injury|injury\s+date|date\s+of\s+accident|accident\s+date)\b/i.test(
        allText,
      )
      if (!hasTreatment || hasInjuryOnly) {
        foundBoxes = []
      }
    }

    return foundBoxes
  }

  // First search the target / active page
  const targetPageNum = activePage || 1
  const targetBoxes = searchPage(targetPageNum)
  if (targetBoxes && targetBoxes.length > 0) {
    highlights[targetPageNum] = targetBoxes
    return highlights
  }

  // Cross-page fallback: search all other loaded pages in document order
  // Guard: Only allow cross-page fallback if entity does NOT have an annotated activePage,
  // or if the match is sufficiently long (>= 10 chars) to prevent false hits on unrelated pages.
  const totalPages = numPages || Object.keys(pageDataMap).length
  for (let p = 1; p <= totalPages; p++) {
    if (p === targetPageNum) continue
    const boxes = searchPage(p)
    if (boxes && boxes.length > 0) {
      // If an activePage was explicitly targeted, require multi-word or long text for cross-page jumping
      if (activePage && activePage > 0) {
        const textLen = (boxes[0].str || '').trim().length
        if (textLen < 12) continue
      }
      highlights[p] = boxes
      return highlights
    }
  }

  // Scanned Document Fallback: If PDF.js found zero native text boxes (e.g. scanned image PDF)
  // and Textract lineBlocks are available, compute highlight boxes from OCR line block coordinates.
  if (lineBlocks && lineBlocks.length > 0) {
    const mapMatchToPixelBox = (m, p) => {
      const baseViewport = pageDataMap[p]?.baseViewport || firstPageViewport
      const cardWidth = baseViewport ? Math.round(baseViewport.width * scale) : Math.round(612 * scale)
      const cardHeight = baseViewport ? Math.round(baseViewport.height * scale) : Math.round(792 * scale)
      return {
        left: Math.round(m.left * cardWidth),
        top: Math.round(m.top * cardHeight),
        width: Math.round(m.width * cardWidth),
        height: Math.round(m.height * cardHeight),
        str: m.text,
        label: m.text,
        priority: m.isPrimary ? 1 : 2,
        idx: m.idx,
        score: m.score,
      }
    }

    // A. Check target page first
    const targetLineMatches = computeImageHighlights({
      lineBlocks,
      activeSnippet,
      activeEntityValue,
      activeCategory,
      activeEntityField,
      activeLineNumber,
      activePage: targetPageNum,
    })

    if (targetLineMatches && targetLineMatches.length > 0) {
      const mapped = targetLineMatches.map(m => mapMatchToPixelBox(m, targetPageNum))
      mapped.sort((a, b) => a.priority - b.priority || (b.score || 0) - (a.score || 0))
      highlights[targetPageNum] = mapped
      return highlights
    }

    // B. Cross-page fallback: check all other pages in lineBlocks
    // High-confidence guard: only jump cross-page if score >= 70 or text length >= 12
    const allLineMatches = computeImageHighlights({
      lineBlocks,
      activeSnippet,
      activeEntityValue,
      activeCategory,
      activeEntityField,
      activeLineNumber,
      activePage: null,
    })

    const strongMatches = (allLineMatches || []).filter(m => m.score >= 70 || (m.text && m.text.trim().length >= 12))
    if (strongMatches.length > 0) {
      const bestPage = strongMatches[0].page || 1
      const pageMatches = strongMatches.filter(m => (m.page || 1) === bestPage)
      const mapped = pageMatches.map(m => mapMatchToPixelBox(m, bestPage))
      mapped.sort((a, b) => a.priority - b.priority || (b.score || 0) - (a.score || 0))
      highlights[bestPage] = mapped
      return highlights
    }
  }

  return highlights
}

// Re-export isDocumentBoilerplateText from entityMatcher for backwards compatibility and test imports
export { isDocumentBoilerplateText } from './entityMatcher'

/**
 * Checks if a text string is a major tabular section division header
 * (e.g. "Insured Information", "Employee Information", "Medical Provider 1").
 * Section headers define scope for nested fields and should not cluster vertically into field rows.
 */
export function isSectionHeader(text = '') {
  if (!text) return false
  const clean = text.trim()
  return (
    /^(?:insured|employer|employee|claimant|patient|medical\s+provider|treating\s+physician|injury|illness|carrier|claims?\s+administrator|insurance\s+carrier)\b.*(?:information|data)?$/i.test(
      clean,
    ) ||
    /^(?:insured\s+information|employee\s+information|medical\s+provider\s+\d+|treating\s+physician|injury\s*\/\s*illness\s+information|claim\s+administrator\s+information)$/i.test(
      clean,
    )
  )
}

/**
 * Checks if a text line looks like a form cell label (e.g. "Name", "Main Address", "Phone", "FEIN").
 * Within a tabular cell, discovering a new label denotes a boundary to the next cell.
 */
export function isFieldLabelLike(text = '') {
  if (!text) return false
  const clean = text.trim()
  if (clean.length < 2) return false

  // Form questions ending in '?' (e.g. "Minor Child?", "Admitted to Hospital?", "Return to work?", "Fatality?")
  if (clean.endsWith('?')) return true

  // Standard form field labels (case-insensitive prefixes and exact matches)
  if (
    /^(?:name|insured|claimant|employee|main\s+address|address|street|city|state|zip|phone|telephone|fax|fein|ssn|dob|date\s+of|time\s+of|policy|carrier|status|gender|sex|treating|diagnosis|nature\s+of|person\s+reporting|person\s+preparing|preparer|claim(?:\s+number)?|occupation|job\s+title|department|branch|division|class\s+code|employment\s+class|minor\s+child|minor|child|wage|salary|rate\s+of\s+pay|pay|hire\s+date|hours\s+worked|days\s+per\s+week|supervisor|witness|hospital|clinic|facility|physician|doctor|provider|first\s+report|notified|employer\s+notified|accident\s+date|injury\s+date|body\s+part|part\s+of\s+body|cause\s+of\s+injury)/i.test(
      clean,
    )
  ) {
    return true
  }

  // Explicit label with trailing colon (e.g. "Employer:", "Date:", "SSN:")
  if (clean.endsWith(':')) return true

  // Known label prefix followed by value (e.g. "Phone: (555) 123-4567", "DOB: 01/01/1980")
  if (
    /^(?:name|insured|claimant|employee|employer|phone|tel|telephone|fax|fein|ssn|dob|date|time|policy|claim|carrier|status|gender|sex|age|occupation|dept|department|wage|salary|hire|doctor|physician|hospital|provider|rx|diag|diagnosis|icd):\s*\S+/i.test(
      clean,
    )
  ) {
    return true
  }

  return false
}

/**
 * Scans upward from the clicked position on the page to determine the enclosing section context
 * (e.g. "insured", "claimant", "provider", "injury", "referral") for disambiguating generic labels like "Name".
 */
export function getContainingSection(targetTop, spanInfos) {
  if (!spanInfos || spanInfos.length === 0) return null
  const sectionHeaders = spanInfos.filter(s => {
    if (s.top > targetTop + 5) return false
    return isSectionHeader(s.text)
  })
  if (sectionHeaders.length === 0) return null
  sectionHeaders.sort((a, b) => b.top - a.top)
  const headerText = sectionHeaders[0].text.toLowerCase()
  if (headerText.includes('insured') || headerText.includes('employer')) return 'insured'
  if (headerText.includes('employee') || headerText.includes('claimant') || headerText.includes('patient'))
    return 'claimant'
  if (headerText.includes('medical provider') || headerText.includes('physician') || headerText.includes('provider'))
    return 'provider'
  if (headerText.includes('injury') || headerText.includes('illness')) return 'injury'
  if (headerText.includes('carrier') || headerText.includes('administrator')) return 'referral'
  return null
}

/**
 * Clusters text spans in a rendered textLayer into containing form cells / multi-line boxes.
 * When a user clicks anywhere in a multi-line box (e.g. on a field label, personal name, or wrapped
 * email line), this detects all related lines in that box, stitches wrapped lines (e.g. "user@medicalc" + "enter.com"),
 * and produces candidate strings for entity matching and action popovers.
 */
export function getBoxClusterInfo(targetSpan, container) {
  if (!targetSpan || !container) return null

  const allSpans = Array.from(container.querySelectorAll('span'))
  if (allSpans.length === 0) return null

  const containerRect = container.getBoundingClientRect ? container.getBoundingClientRect() : null
  const spanInfos = allSpans
    .map(s => {
      let top = parseFloat(s.style.top || 0)
      let left = parseFloat(s.style.left || 0)
      let width = s.offsetWidth || parseFloat(s.style.width || 40)
      let height = s.offsetHeight || parseFloat(s.style.height || s.style.fontSize || 12)

      if (containerRect && s.getBoundingClientRect) {
        const r = s.getBoundingClientRect()
        if (r.width > 0 && r.height > 0) {
          left = r.left - containerRect.left
          top = r.top - containerRect.top
          width = r.width
          height = r.height
        }
      }

      return {
        span: s,
        text: (s.textContent || '').trim(),
        top,
        left,
        right: left + width,
        bottom: top + height,
      }
    })
    .filter(s => s.text.length > 0)

  const targetTop = parseFloat(targetSpan.style.top || 0)
  const targetLeft = parseFloat(targetSpan.style.left || 0)
  const targetWidth = targetSpan.offsetWidth || parseFloat(targetSpan.style.width || 40)
  const targetHeight = targetSpan.offsetHeight || parseFloat(targetSpan.style.height || targetSpan.style.fontSize || 12)

  const targetInfo = spanInfos.find(s => s.span === targetSpan) || {
    span: targetSpan,
    text: (targetSpan.textContent || '').trim(),
    top: targetTop,
    left: targetLeft,
    right: targetLeft + targetWidth,
    bottom: targetTop + targetHeight,
  }

  // 1. Spans on the same visual line (within 6px vertically, horizontal gap < 16px to prevent merging adjacent columns)
  const sameLineSpans = spanInfos.filter(s => Math.abs(s.top - targetInfo.top) < 6)
  sameLineSpans.sort((a, b) => a.left - b.left)

  let tIdx = sameLineSpans.findIndex(s => s.span === targetSpan)
  if (tIdx === -1) {
    tIdx = sameLineSpans.findIndex(s => Math.abs(s.left - targetInfo.left) < 10)
  }

  let lStart = tIdx >= 0 ? tIdx : 0
  while (lStart > 0 && sameLineSpans[lStart].left - sameLineSpans[lStart - 1].right < 16) {
    // If adjacent span to the left is already a distinct field label or question, stop at column boundary
    if (isFieldLabelLike(sameLineSpans[lStart].text) || isFieldLabelLike(sameLineSpans[lStart - 1].text)) {
      break
    }
    lStart--
  }
  let lEnd = tIdx >= 0 ? tIdx : sameLineSpans.length - 1
  while (lEnd < sameLineSpans.length - 1 && sameLineSpans[lEnd + 1].left - sameLineSpans[lEnd].right < 16) {
    // If adjacent span to the right is a distinct field label or question, stop at column boundary
    if (isFieldLabelLike(sameLineSpans[lEnd + 1].text)) {
      break
    }
    lEnd++
  }

  const currentLineSpans = sameLineSpans.slice(lStart, lEnd + 1)
  const currentLineBox = {
    left: Math.min(...currentLineSpans.map(s => s.left)),
    right: Math.max(...currentLineSpans.map(s => s.right)),
    top: Math.min(...currentLineSpans.map(s => s.top)),
    bottom: Math.max(...currentLineSpans.map(s => s.bottom)),
    spans: currentLineSpans,
    text: currentLineSpans
      .map(s => s.text)
      .join(' ')
      .trim(),
  }

  // If the clicked line is a section header banner, it is self-contained and should not cluster with other cells
  if (isSectionHeader(currentLineBox.text)) {
    return {
      clickedLineText: currentLineBox.text,
      stitchedFullBox: currentLineBox.text,
      stitchedValue: '',
      stitchedEmail: '',
      boxLabel: currentLineBox.text,
      allLineBoxes: [currentLineBox],
      isSectionHeader: true,
      bounds: {
        left: currentLineBox.left,
        right: currentLineBox.right,
        top: currentLineBox.top,
        bottom: currentLineBox.bottom,
      },
    }
  }

  // 2. Walk UPWARDS to find preceding lines in the same box
  const linesAbove = []
  // If the clicked line is already a field label (e.g. "Department / Branch / Division" or "Minor Child?"),
  // it is by definition the top of the cell/box — never walk upward into preceding cells/rows.
  const isCurrentLineLabel = isFieldLabelLike(currentLineBox.text)

  if (!isCurrentLineLabel) {
    let curBox = currentLineBox
    while (linesAbove.length < 5) {
      if (isFieldLabelLike(curBox.text)) break

      const candidates = spanInfos.filter(s => {
        const vGap = curBox.top - s.bottom
        // In tabular forms, lines within a cell have tight line-spacing (1px to 12px).
        // Larger gaps indicate crossing into the preceding table row.
        if (vGap < -2 || vGap > 12) return false
        if (isSectionHeader(s.text)) return false
        // Require strict horizontal column alignment within the same cell
        const leftDiff = Math.abs(curBox.left - s.left)
        const hOverlap = Math.min(curBox.right, s.right) - Math.max(curBox.left, s.left)
        return leftDiff <= 16 && hOverlap > 10
      })
      if (candidates.length === 0) break
      candidates.sort((a, b) => b.top - a.top)
      const candTop = candidates[0].top
      const sameLineCands = candidates.filter(s => Math.abs(s.top - candTop) < 5)
      sameLineCands.sort((a, b) => a.left - b.left)
      const candBox = {
        left: Math.min(...sameLineCands.map(s => s.left)),
        right: Math.max(...sameLineCands.map(s => s.right)),
        top: Math.min(...sameLineCands.map(s => s.top)),
        bottom: Math.max(...sameLineCands.map(s => s.bottom)),
        spans: sameLineCands,
        text: sameLineCands
          .map(s => s.text)
          .join(' ')
          .trim(),
      }
      linesAbove.unshift(candBox)
      curBox = candBox
    }
  }

  // 3. Walk DOWNWARDS to find subsequent lines in the same box
  const linesBelow = []
  let curBox = currentLineBox
  while (linesBelow.length < 5) {
    const candidates = spanInfos.filter(s => {
      const vGap = s.top - curBox.bottom
      // Strict line-spacing within a single cell: max 12px
      if (vGap < -2 || vGap > 12) return false
      if (isSectionHeader(s.text)) return false
      // Do not walk into the next field label or question
      if (isFieldLabelLike(s.text)) return false
      // Require strict horizontal column alignment
      const leftDiff = Math.abs(curBox.left - s.left)
      const hOverlap = Math.min(curBox.right, s.right) - Math.max(curBox.left, s.left)
      return leftDiff <= 16 && hOverlap > 10
    })
    if (candidates.length === 0) break
    candidates.sort((a, b) => a.top - b.top)
    const candTop = candidates[0].top
    const sameLineCands = candidates.filter(s => Math.abs(s.top - candTop) < 5)
    sameLineCands.sort((a, b) => a.left - b.left)
    const candBox = {
      left: Math.min(...sameLineCands.map(s => s.left)),
      right: Math.max(...sameLineCands.map(s => s.right)),
      top: Math.min(...sameLineCands.map(s => s.top)),
      bottom: Math.max(...sameLineCands.map(s => s.bottom)),
      spans: sameLineCands,
      text: sameLineCands
        .map(s => s.text)
        .join(' ')
        .trim(),
    }
    linesBelow.push(candBox)
    curBox = candBox
  }

  const allLineBoxes = [...linesAbove, currentLineBox, ...linesBelow]

  // Intelligent line stitching (handles hyphenation and wrapped tokens/emails/URLs)
  const stitchLineList = list => {
    const texts = list.map(l => l.text.trim()).filter(Boolean)
    if (texts.length === 0) return ''
    if (texts.length === 1) return texts[0]
    let res = texts[0]
    for (let i = 1; i < texts.length; i++) {
      const prev = res
      const cur = texts[i]
      if (prev.endsWith('-') && !prev.endsWith(' -')) {
        res = prev.slice(0, -1) + cur
      } else if (
        /[@._/a-zA-Z0-9]$/.test(prev) &&
        /^[a-zA-Z0-9._-]/.test(cur) &&
        (prev.includes('@') ||
          cur.includes('.com') ||
          cur.includes('.org') ||
          cur.includes('.edu') ||
          cur.includes('.gov') ||
          cur.includes('.net') ||
          prev.includes('http'))
      ) {
        res = prev + cur
      } else {
        res = prev + ' ' + cur
      }
    }
    return res.trim()
  }

  const stitchedFullBox = stitchLineList(allLineBoxes)

  // Identify if first line is a field label (e.g. "Person Reporting Claim", "Address:", "Policy Period")
  const firstLineText = allLineBoxes[0]?.text || ''
  const isLabel = isFieldLabelLike(firstLineText)

  const valueLineBoxes = isLabel && allLineBoxes.length > 1 ? allLineBoxes.slice(1) : allLineBoxes
  const stitchedValue = isLabel && allLineBoxes.length === 1 ? '' : stitchLineList(valueLineBoxes)

  // Stitched email / URL / token if any line in the box wraps an email
  let stitchedEmail = ''
  const emailLineBoxes = allLineBoxes.filter(
    l => l.text.includes('@') || /\.(?:com|org|edu|gov|net|io)\b/i.test(l.text),
  )
  if (emailLineBoxes.length > 0) {
    stitchedEmail = stitchLineList(emailLineBoxes)
  }

  const bounds = {
    left: Math.min(...allLineBoxes.map(l => l.left)),
    right: Math.max(...allLineBoxes.map(l => l.right)),
    top: Math.min(...allLineBoxes.map(l => l.top)),
    bottom: Math.max(...allLineBoxes.map(l => l.bottom)),
  }

  // Structural header/footer classification (Universal layout detection)
  const pageHeight =
    parseFloat(container.style?.height || 0) ||
    container.clientHeight ||
    (container.getBoundingClientRect ? container.getBoundingClientRect().height : 0) ||
    792
  const isTopHeaderZone = bounds.top > 0 && bounds.top < pageHeight * 0.12
  const hasDisplayHeadingFont = allLineBoxes.some(
    b => b.spans && b.spans.some(s => parseFloat(s.style?.fontSize || 0) >= 13 || s.offsetHeight >= 14),
  )
  const isDocumentHeader =
    isTopHeaderZone && !isLabel && (hasDisplayHeadingFont || isDocumentBoilerplateText(stitchedFullBox))
  const isDocumentFooter = bounds.top > pageHeight * 0.94 && !isLabel

  return {
    clickedLineText: currentLineBox.text,
    stitchedFullBox,
    stitchedValue,
    stitchedEmail,
    boxLabel: isLabel ? firstLineText : '',
    allLineBoxes,
    bounds,
    isDocumentHeader,
    isDocumentFooter,
  }
}

export default function PdfCanvasViewer({
  url,
  lineBlocks = [],
  activePage = 1,
  activeLineNumber = null,
  activeSnippet = '',
  activeCategory = '',
  activeEntityField = '',
  activeEntityValue = '',
  fileName = 'Document.pdf',
  entities = [],
  onSnippetClick,
  onShowPopover,
}) {
  const containerRef = useRef(null)
  const [pdfDoc, setPdfDoc] = useState(null)
  const [numPages, setNumPages] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [scale, setScale] = useState(1.1)
  const [firstPageViewport, setFirstPageViewport] = useState(null)
  const [pageDataMap, setPageDataMap] = useState({}) // { [pageNum]: { baseViewport, items } }
  const [visiblePages, setVisiblePages] = useState(() => new Set([activePage || 1]))

  const pageContainerRefs = useRef({})
  const activeHighlightRef = useRef(null)
  const [clickedBlock, setClickedBlock] = useState(null)

  useEffect(() => {
    setClickedBlock(null)
  }, [activePage, activeEntityField, activeSnippet, url])

  const handlePdfTextClick = (e, pageNum) => {
    const selection = window.getSelection()
    if (selection && !selection.isCollapsed && selection.toString().trim().length >= 2) {
      return
    }

    if (!e.target || e.target.nodeType !== 1) {
      setClickedBlock(null)
      return
    }

    const clickedRawText = (e.target.textContent || '').trim()

    const container = e.currentTarget || e.target.parentElement
    const clusterInfo = getBoxClusterInfo(e.target, container)
    const clickedLine = clusterInfo ? clusterInfo.clickedLineText : clickedRawText

    // 1. Boilerplate & Document Chrome Suppression:
    // Suppress clicks on document title banners, page headers, footers, and statutory boilerplate
    if (
      clusterInfo?.isDocumentHeader ||
      clusterInfo?.isDocumentFooter ||
      isDocumentBoilerplateText(clickedRawText) ||
      (clusterInfo && isDocumentBoilerplateText(clusterInfo.stitchedFullBox))
    ) {
      setClickedBlock(null)
      return
    }

    // Establish active clicked form cell / block highlight overlay
    if (clusterInfo?.bounds) {
      const b = clusterInfo.bounds
      setClickedBlock({
        pageNum,
        left: Math.max(0, b.left - 4),
        top: Math.max(0, b.top - 4),
        width: Math.max(20, b.right - b.left + 8),
        height: Math.max(16, b.bottom - b.top + 8),
        label: clusterInfo.boxLabel || '',
        value: clusterInfo.stitchedValue || clickedLine,
        text: clusterInfo.stitchedFullBox || clickedLine,
      })
    } else {
      setClickedBlock(null)
    }

    // 2. Section Header Click: isolated banner, should not match field entities
    if (clusterInfo?.isSectionHeader) {
      if (onShowPopover) {
        const rect = e.target.getBoundingClientRect
          ? e.target.getBoundingClientRect()
          : { left: e.clientX, top: e.clientY, width: 140, height: 24 }
        onShowPopover({
          text: clickedLine,
          sourceSnippet: clickedLine,
          boxLabel: 'Section',
          page: pageNum,
          coords: {
            x: rect.left,
            y: rect.top,
            width: Math.min(rect.width || 140, 360),
            height: rect.height || 24,
          },
          matchedEntity: null,
        })
      }
      return
    }

    // 3. Compute sectionContext to disambiguate generic labels like "Name", "Main Address", "Phone"
    const allSpans = Array.from(container.querySelectorAll('span'))
    const containerRect = container.getBoundingClientRect ? container.getBoundingClientRect() : null
    const spanInfos = allSpans.map(s => {
      let top = parseFloat(s.style.top || 0)
      if (containerRect && s.getBoundingClientRect) {
        const r = s.getBoundingClientRect()
        if (r.width > 0) top = r.top - containerRect.top
      }
      return { text: (s.textContent || '').trim(), top }
    })
    const targetTop = clusterInfo?.bounds ? clusterInfo.bounds.top : parseFloat(e.target.style.top || 0)
    const sectionContext = getContainingSection(targetTop, spanInfos)

    // 4. Value-First Matching Pipeline:
    // A cell's extracted value (e.g. "Grace Manor Care Center") is specific and unique, whereas generic
    // labels ("Name") appear across multiple sections (Insured vs Employee vs Provider).
    let matched = null

    // A. Check stitched cell value first (highest specificity)
    if (clusterInfo?.stitchedValue) {
      matched = findMatchingEntity(clusterInfo.stitchedValue, entities, { sectionContext })
    }
    // B. Check stitched email / token
    if (!matched && clusterInfo?.stitchedEmail) {
      matched = findMatchingEntity(clusterInfo.stitchedEmail, entities, { sectionContext })
    }
    // C. Check box field label with sectionContext disambiguation
    if (!matched && clusterInfo?.boxLabel) {
      matched = findMatchingEntity(clusterInfo.boxLabel, entities, { sectionContext })
    }
    // D. Check clicked line with sectionContext
    if (!matched && clickedLine && clickedLine !== clusterInfo?.boxLabel) {
      matched = findMatchingEntity(clickedLine, entities, { sectionContext })
    }
    // E. Full box text fallback
    if (!matched && clusterInfo?.stitchedFullBox) {
      matched = findMatchingEntity(clusterInfo.stitchedFullBox, entities, { sectionContext })
    }

    if (matched && onSnippetClick) {
      onSnippetClick(matched.extractedValue || matched.field, matched.id)
    }

    // Always provide actions on the click target via onShowPopover:
    if (onShowPopover) {
      // Anchor popover to the enclosing cell cluster bounding rect if available
      let blockRect = null
      try {
        const allSpans = clusterInfo?.allLineBoxes
          ? clusterInfo.allLineBoxes.flatMap(b => b.spans.map(s => s.span)).filter(Boolean)
          : []
        if (allSpans.length > 0) {
          const rects = allSpans.map(s => s.getBoundingClientRect && s.getBoundingClientRect()).filter(Boolean)
          if (rects.length > 0) {
            const minL = Math.min(...rects.map(r => r.left))
            const maxR = Math.max(...rects.map(r => r.right))
            const minT = Math.min(...rects.map(r => r.top))
            const maxB = Math.max(...rects.map(r => r.bottom))
            blockRect = {
              left: minL - 2,
              top: minT - 2,
              width: Math.max(40, maxR - minL + 4),
              height: Math.max(20, maxB - minT + 4),
            }
          }
        }
      } catch {}

      const rect =
        blockRect ||
        (e.target.getBoundingClientRect
          ? e.target.getBoundingClientRect()
          : { left: e.clientX, top: e.clientY, width: 140, height: 24 })

      // Choose coherent text for popover:
      // 1. If clicked line is a label or question (e.g. "Department / Branch / Division", "Person Reporting Claim", "Minor Child?"), offer clicked line.
      // 2. If clicked line is part of a wrapped email/token, offer the stitched email.
      // 3. If clicked line is the box label, offer the box's stitched value.
      // 4. If the box is a short multi-line value (e.g. 2 lines without subfields), offer stitched value.
      // 5. Otherwise, offer the clicked line text.
      let popoverText = clickedLine
      if (isFieldLabelLike(clickedLine)) {
        popoverText = clickedLine
      } else if (
        clusterInfo?.stitchedEmail &&
        (clickedLine.includes('@') ||
          /\.(?:com|org|edu|gov|net|io)\b/i.test(clickedLine) ||
          clusterInfo.stitchedEmail.includes(clickedLine))
      ) {
        popoverText = clusterInfo.stitchedEmail
      } else if (clusterInfo?.boxLabel && clickedLine === clusterInfo.boxLabel && clusterInfo.stitchedValue) {
        popoverText = clusterInfo.stitchedValue
      } else if (
        clusterInfo?.stitchedValue &&
        clusterInfo.stitchedValue.length < 60 &&
        !clusterInfo.stitchedValue.includes('\n') &&
        clusterInfo.allLineBoxes?.length <= 2
      ) {
        popoverText = clusterInfo.stitchedValue
      }

      onShowPopover({
        text: popoverText,
        sourceSnippet: clusterInfo?.stitchedFullBox || popoverText,
        boxLabel: clusterInfo?.boxLabel || '',
        stitchedEmail: clusterInfo?.stitchedEmail || '',
        stitchedValue: clusterInfo?.stitchedValue || '',
        page: pageNum,
        coords: {
          x: rect.left,
          y: rect.top,
          width: Math.min(rect.width || 140, 360),
          height: rect.height || 24,
        },
        matchedEntity: matched || null,
      })
    }
  }

  const handlePdfMouseUp = (e, pageNum) => {
    if (typeof window === 'undefined') return
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed) return
    const text = selection.toString().trim()
    if (text.length < 2) return

    try {
      const range = selection.getRangeAt(0)
      const rect = range.getBoundingClientRect()

      const matched = findMatchingEntity(text, entities)

      if (onShowPopover) {
        onShowPopover({
          text,
          sourceSnippet: text,
          page: pageNum || activePage || 1,
          coords: {
            x: rect.left,
            y: rect.top,
            width: rect.width,
            height: rect.height,
          },
          matchedEntity: matched || null,
        })
      }
    } catch {}
  }

  // 1. Load the PDF document
  useEffect(() => {
    if (!url) {
      setLoading(false)
      setError('No document URL provided.')
      return
    }

    let isCancelled = false
    setLoading(true)
    setError(null)
    setPdfDoc(null)
    setPageDataMap({})
    setFirstPageViewport(null)

    const isBlob = typeof url === 'string' && url.startsWith('blob:')
    const loadingTask = pdfjsLib.getDocument({
      url,
      withCredentials: !isBlob,
    })

    loadingTask.promise
      .then(async doc => {
        if (isCancelled) return
        setPdfDoc(doc)
        setNumPages(doc.numPages)
        setLoading(false)

        try {
          const p1 = await doc.getPage(1)
          if (!isCancelled) {
            const vp = p1.getViewport({ scale: 1 })
            setFirstPageViewport(vp)
          }
        } catch {}
      })
      .catch(err => {
        if (isCancelled) return
        // eslint-disable-next-line no-console
        console.error('Failed to load PDF in Canvas Viewer:', err)
        setError(err.message || 'Failed to load PDF.')
        setLoading(false)
      })

    return () => {
      isCancelled = true
      if (loadingTask && loadingTask.destroy) {
        try {
          loadingTask.destroy()
        } catch {}
      }
    }
  }, [url])

  // Fit Width helper
  const handleFitWidth = () => {
    if (!containerRef.current || !firstPageViewport) return
    const containerWidth = containerRef.current.clientWidth - 48
    if (containerWidth > 200) {
      const fitScale = +(containerWidth / firstPageViewport.width).toFixed(2)
      setScale(Math.max(0.4, Math.min(2.2, fitScale)))
    }
  }

  // Fit Page helper (fits entire page height and width within the visible scroll container)
  const handleFitPage = () => {
    if (!containerRef.current || !firstPageViewport) return
    const containerWidth = containerRef.current.clientWidth - 48
    const containerHeight = containerRef.current.clientHeight - 64
    if (containerWidth > 150 && containerHeight > 150) {
      const widthScale = containerWidth / firstPageViewport.width
      const heightScale = containerHeight / firstPageViewport.height
      const fitScale = +Math.min(widthScale, heightScale).toFixed(2)
      setScale(Math.max(0.4, Math.min(2.2, fitScale)))
    }
  }

  // Set initial scale to fit nicely in container (taking smaller viewport heights into account)
  useEffect(() => {
    if (firstPageViewport && containerRef.current) {
      const containerWidth = containerRef.current.clientWidth - 48
      const containerHeight = containerRef.current.clientHeight - 64
      if (containerWidth > 200) {
        const widthScale = containerWidth / firstPageViewport.width
        const heightScale = containerHeight > 180 ? containerHeight / firstPageViewport.height : widthScale
        const preferredScale =
          containerHeight > 180 && containerHeight < 560
            ? Math.min(widthScale, Math.max(0.65, heightScale * 1.15))
            : widthScale
        setScale(Math.max(0.6, Math.min(1.2, +preferredScale.toFixed(2))))
      }
    }
  }, [firstPageViewport])

  // 2. Extract text and viewports with priority for active target page and streaming remaining pages
  useEffect(() => {
    if (!pdfDoc) {
      setPageDataMap({})
      return
    }

    let isCancelled = false

    const extractPageData = async p => {
      try {
        const page = await pdfDoc.getPage(p)
        const baseViewport = page.getViewport({ scale: 1 })
        const rawContent = await page.getTextContent()
        const sortedItems = sortTextItemsByLayout(rawContent.items)
        const textContent = {
          ...rawContent,
          items: sortedItems,
        }
        return {
          baseViewport,
          textContent,
          items: sortedItems.map(item => ({
            str: item.str,
            cleanStr: normalizeText(item.str),
            transform: item.transform,
            width: item.width,
            height: item.height,
          })),
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`Could not extract text from page ${p}:`, err)
        return null
      }
    }

    const runExtraction = async () => {
      const targetP = activePage || 1

      // 1. Immediately extract active target page
      const targetData = await extractPageData(targetP)
      if (isCancelled) return
      if (targetData) {
        setPageDataMap(prev => ({ ...prev, [targetP]: targetData }))
      }

      // 2. Extract nearby pages (+/- 5)
      const nearbyPages = []
      for (let offset = 1; offset <= 5; offset++) {
        if (targetP - offset >= 1) nearbyPages.push(targetP - offset)
        if (targetP + offset <= pdfDoc.numPages) nearbyPages.push(targetP + offset)
      }
      for (const p of nearbyPages) {
        if (isCancelled) return
        const data = await extractPageData(p)
        if (data && !isCancelled) {
          setPageDataMap(prev => ({ ...prev, [p]: data }))
        }
      }

      // 3. Incrementally extract remaining pages in chunks of 5 with yield
      const remainingPages = []
      for (let p = 1; p <= pdfDoc.numPages; p++) {
        if (p !== targetP && !nearbyPages.includes(p)) {
          remainingPages.push(p)
        }
      }

      for (let i = 0; i < remainingPages.length; i += 5) {
        if (isCancelled) return
        const batch = remainingPages.slice(i, i + 5)
        const results = await Promise.all(batch.map(p => extractPageData(p)))
        if (isCancelled) return
        setPageDataMap(prev => {
          const next = { ...prev }
          batch.forEach((p, idx) => {
            if (results[idx]) next[p] = results[idx]
          })
          return next
        })
        await new Promise(resolve => setTimeout(resolve, 25))
      }
    }

    runExtraction()

    return () => {
      isCancelled = true
    }
  }, [pdfDoc, activePage])

  // 3. Compute active highlight boxes dynamically using pure memoization
  const activeHighlightsForPage = useMemo(() => {
    return computeActiveHighlights({
      pageDataMap,
      lineBlocks,
      firstPageViewport,
      scale,
      activeSnippet,
      activeEntityValue,
      activeCategory,
      activeEntityField,
      activePage,
      activeLineNumber,
      numPages,
    })
  }, [
    pageDataMap,
    lineBlocks,
    firstPageViewport,
    scale,
    activeSnippet,
    activeEntityValue,
    activeCategory,
    activeEntityField,
    activePage,
    activeLineNumber,
    numPages,
  ])

  const highlightedPageStr = Object.keys(activeHighlightsForPage).find(
    p => (activeHighlightsForPage[p] || []).length > 0,
  )
  const activePageDisplay = highlightedPageStr ? parseInt(highlightedPageStr, 10) : activePage || 1

  // 4. Virtualize page visibility using IntersectionObserver
  useEffect(() => {
    if (!containerRef.current || numPages === 0) return

    if (typeof IntersectionObserver !== 'undefined') {
      const observer = new IntersectionObserver(
        entries => {
          setVisiblePages(prev => {
            let changed = false
            const next = new Set(prev)
            entries.forEach(entry => {
              const pNum = parseInt(entry.target.getAttribute('data-page-num'), 10)
              if (!isNaN(pNum)) {
                if (entry.isIntersecting) {
                  if (!next.has(pNum)) {
                    next.add(pNum)
                    changed = true
                  }
                } else {
                  if (Math.abs(pNum - activePageDisplay) > 2) {
                    if (next.has(pNum)) {
                      next.delete(pNum)
                      changed = true
                    }
                  }
                }
              }
            })
            return changed ? next : prev
          })
        },
        {
          root: containerRef.current,
          rootMargin: '600px 0px 600px 0px',
          threshold: 0,
        },
      )

      Object.values(pageContainerRefs.current).forEach(el => {
        if (el) observer.observe(el)
      })

      return () => {
        observer.disconnect()
      }
    }
  }, [numPages, activePageDisplay, loading])

  // 5. Smooth auto-scroll to active page or highlighted text without flashing, strictly confined to containerRef
  useEffect(() => {
    if (loading || !pdfDoc) return

    const scrollToActiveTarget = (smooth = true) => {
      const container = containerRef.current
      if (!container) return

      const targetEl = activeHighlightRef.current || pageContainerRefs.current[activePageDisplay]
      if (!targetEl) return

      const containerRect = container.getBoundingClientRect()
      const targetRect = targetEl.getBoundingClientRect()
      const relativeTop = targetRect.top - containerRect.top + container.scrollTop

      let targetScrollTop = relativeTop
      if (activeHighlightRef.current && targetEl === activeHighlightRef.current) {
        // Center the highlight vertically in the container
        targetScrollTop = Math.max(0, relativeTop - container.clientHeight / 2 + targetRect.height / 2)
      } else {
        // Align page to the top with a comfortable margin
        targetScrollTop = Math.max(0, relativeTop - 16)
      }

      if (typeof container.scrollTo === 'function') {
        container.scrollTo({
          top: targetScrollTop,
          behavior: smooth ? 'smooth' : 'auto',
        })
      } else {
        container.scrollTop = targetScrollTop
      }
    }

    const timer = setTimeout(() => scrollToActiveTarget(true), 150)

    const containerEl = containerRef.current
    let resizeObserver = null
    if (containerEl && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (activeHighlightRef.current) {
          scrollToActiveTarget(false)
        }
      })
      resizeObserver.observe(containerEl)
    }

    return () => {
      clearTimeout(timer)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
    }
  }, [activePageDisplay, activeSnippet, activeEntityValue, loading, pdfDoc, activeHighlightsForPage])

  return (
    <div
      className="pdf-canvas-viewer-root d-flex flex-column flex-grow-1 position-relative"
      style={{ flex: '1 1 0%', minHeight: 0, overflow: 'hidden' }}
    >
      {/* Viewer Toolbar */}
      <div
        className="pdf-canvas-toolbar px-3 py-2 bg-light border-bottom d-flex align-items-center justify-content-between"
        style={{ minHeight: '44px', gap: '8px', overflowX: 'auto', flexWrap: 'nowrap', flexShrink: 0 }}
      >
        <div className="d-flex align-items-center" style={{ gap: '10px', minWidth: 0, flexShrink: 1 }}>
          <span
            className="font-weight-bold text-dark small text-truncate"
            style={{ fontSize: '0.82rem', maxWidth: '220px' }}
            title={fileName}
          >
            <i className="fas fa-file-pdf text-danger mr-1" /> {fileName}
          </span>
          {numPages > 0 && (
            <span className="badge badge-secondary" style={{ fontSize: '0.75rem', flexShrink: 0 }}>
              Page {activePageDisplay} of {numPages}
            </span>
          )}
        </div>

        {/* Zoom & Direct Open Controls */}
        <div className="d-flex align-items-center" style={{ gap: '8px' }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-xs"
            onClick={handleFitPage}
            title="Fit entire page within container"
            style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
          >
            <i className="fas fa-expand-arrows-alt mr-1" /> Fit Page
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary btn-xs"
            onClick={handleFitWidth}
            title="Fit document to container width"
            style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
          >
            <i className="fas fa-arrows-alt-h mr-1" /> Fit Width
          </button>
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className="btn btn-outline-secondary btn-xs"
              onClick={() => setScale(s => Math.max(0.4, +(s - 0.15).toFixed(2)))}
              title="Zoom out"
              style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            >
              <i className="fas fa-search-minus" />
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-xs font-weight-bold"
              onClick={() => setScale(1.1)}
              title="Reset Zoom to 110%"
              style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            >
              {Math.round(scale * 100)}%
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary btn-xs"
              onClick={() => setScale(s => Math.min(2.4, +(s + 0.15).toFixed(2)))}
              title="Zoom in"
              style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
            >
              <i className="fas fa-search-plus" />
            </button>
          </div>

          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-outline-secondary btn-xs"
            title="Open original PDF in browser tab"
            style={{ padding: '0.15rem 0.5rem', fontSize: '0.75rem' }}
          >
            <i className="fas fa-external-link-alt" />
          </a>
        </div>
      </div>

      {/* Main Canvas Scroll Area */}
      <div
        ref={containerRef}
        className="pdf-canvas-scroll-container flex-grow-1 overflow-auto p-3"
        style={{
          display: 'block',
          width: '100%',
          flex: '1 1 0%',
          minHeight: 0,
        }}
      >
        {loading && (
          <div className="my-5 py-5 text-center text-muted">
            <div
              className="spinner-border text-primary mb-3"
              role="status"
              style={{ width: '2.5rem', height: '2.5rem' }}
            >
              <span className="sr-only">Loading PDF...</span>
            </div>
            <div className="font-weight-bold">Loading PDF document…</div>
            <small className="text-muted">Preparing high-resolution pages and highlights</small>
          </div>
        )}

        {error && !loading && (
          <div className="alert alert-warning my-4 mx-auto p-4 text-left" style={{ maxWidth: '600px' }}>
            <h6>
              <i className="fas fa-exclamation-triangle mr-2 text-warning" />
              Unable to render PDF in canvas
            </h6>
            <p className="small text-muted mb-3">{error}</p>
            <a href={url} target="_blank" rel="noopener noreferrer" className="btn btn-primary btn-sm font-weight-bold">
              <i className="fas fa-external-link-alt mr-1" /> Open PDF directly
            </a>
          </div>
        )}

        {!loading && !error && numPages > 0 && (
          <div className="pdf-pages-stream w-100" style={{ display: 'block', maxWidth: '100%', paddingBottom: '64px' }}>
            {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => {
              const isTargetPage = pageNum === activePageDisplay
              const pageHighlights = activeHighlightsForPage[pageNum] || []
              const baseViewport = pageDataMap[pageNum]?.baseViewport || firstPageViewport
              const pageCardWidth = baseViewport ? Math.round(baseViewport.width * scale) : Math.round(612 * scale)
              const pageCardHeight = baseViewport ? Math.round(baseViewport.height * scale) : Math.round(792 * scale)
              const isPageVisible = Math.abs(pageNum - activePageDisplay) <= 2 || visiblePages.has(pageNum)

              return (
                /* eslint-disable-next-line jsx-a11y/no-static-element-interactions */
                <div
                  key={pageNum}
                  id={`hitl-pdf-page-${pageNum}`}
                  data-page-num={pageNum}
                  ref={el => {
                    pageContainerRefs.current[pageNum] = el
                  }}
                  className={`pdf-page-card mb-4 position-relative ${isTargetPage ? 'active-target-page' : ''}`}
                  onMouseUp={e => handlePdfMouseUp(e, pageNum)}
                  style={{
                    backgroundColor: '#ffffff',
                    boxShadow: isTargetPage
                      ? '0 0 0 3px #ffc107, 0 8px 24px rgba(0,0,0,0.15)'
                      : '0 4px 16px rgba(0,0,0,0.1)',
                    borderRadius: '4px',
                    display: 'block',
                    margin: '0 auto 1.5rem auto',
                    width: `${pageCardWidth}px`,
                    transition: 'box-shadow 0.2s ease',
                  }}
                >
                  {/* Page Top Header */}
                  <div className="pdf-page-card-header px-3 py-1 bg-light border-bottom d-flex justify-content-between align-items-center">
                    <span className="small text-muted font-weight-bold" style={{ fontSize: '0.72rem' }}>
                      Page {pageNum} of {numPages}
                    </span>
                    {isTargetPage && (
                      <span className="badge badge-warning text-dark font-weight-bold" style={{ fontSize: '0.7rem' }}>
                        <i className="fas fa-crosshairs mr-1" /> Current Extraction Target
                      </span>
                    )}
                  </div>

                  {/* Page Canvas Container with Absolute Highlight Overlays */}
                  <div
                    className="position-relative"
                    style={{
                      width: `${pageCardWidth}px`,
                      minHeight: `${pageCardHeight}px`,
                      overflow: 'hidden',
                    }}
                  >
                    {isPageVisible ? (
                      <>
                        <PageCanvas pdfDoc={pdfDoc} pageNum={pageNum} scale={scale} baseViewport={baseViewport} />
                        <PageTextLayer
                          pdfDoc={pdfDoc}
                          pageNum={pageNum}
                          pageData={pageDataMap[pageNum]}
                          scale={scale}
                          onTextClick={handlePdfTextClick}
                        />

                        {/* Active Clicked Form Box / Cell Block Highlight Overlay */}
                        {clickedBlock && clickedBlock.pageNum === pageNum && (
                          <div
                            className="hitl-clicked-block-highlight"
                            data-testid="clicked-block-highlight"
                            style={{
                              position: 'absolute',
                              left: `${clickedBlock.left}px`,
                              top: `${clickedBlock.top}px`,
                              width: `${clickedBlock.width}px`,
                              height: `${clickedBlock.height}px`,
                              backgroundColor: 'rgba(33, 150, 243, 0.14)',
                              border: '2px solid rgba(25, 118, 210, 0.85)',
                              borderRadius: '4px',
                              boxShadow: '0 0 10px rgba(33, 150, 243, 0.35)',
                              pointerEvents: 'none',
                              zIndex: 11,
                              transition: 'all 0.12s ease-out',
                            }}
                          >
                            {clickedBlock.label && (
                              <span
                                style={{
                                  position: 'absolute',
                                  top: '-18px',
                                  left: '0px',
                                  backgroundColor: '#1976d2',
                                  color: '#ffffff',
                                  fontSize: '0.68rem',
                                  fontWeight: 'bold',
                                  padding: '1px 6px',
                                  borderRadius: '3px 3px 0 0',
                                  whiteSpace: 'nowrap',
                                  letterSpacing: '0.3px',
                                  boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
                                }}
                              >
                                {clickedBlock.label}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Source Field Highlight Overlays positioned over actual PDF text */}
                        {pageHighlights.map((hl, hlIdx) => {
                          const isPrimary = isTargetPage && hlIdx === 0
                          return (
                            <div
                              key={hlIdx}
                              ref={isPrimary ? activeHighlightRef : null}
                              className={`pdf-highlight-box ${isPrimary ? 'primary-highlight' : ''}`}
                              style={{
                                position: 'absolute',
                                left: `${hl.left - 2}px`,
                                top: `${hl.top - 2}px`,
                                width: `${hl.width + 4}px`,
                                height: `${hl.height + 4}px`,
                                backgroundColor: isPrimary ? 'rgba(255, 214, 0, 0.28)' : 'rgba(255, 235, 59, 0.18)',
                                border: isPrimary
                                  ? '2px solid rgba(255, 152, 0, 0.85)'
                                  : '1px dashed rgba(245, 127, 23, 0.55)',
                                borderRadius: '3px',
                                boxShadow: isPrimary ? '0 0 8px rgba(255, 152, 0, 0.45)' : 'none',
                                mixBlendMode: 'multiply',
                                pointerEvents: 'auto',
                                cursor: 'pointer',
                                zIndex: 10,
                                animation: isPrimary ? 'pdfGlow 1.5s infinite alternate' : 'none',
                              }}
                              title={`Source Evidence for ${activeEntityField || 'Entity'}: "${hl.str}"`}
                              onClick={() => onSnippetClick && onSnippetClick(hl.str)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={e => {
                                if (e.key === 'Enter') onSnippetClick && onSnippetClick(hl.str)
                              }}
                            />
                          )
                        })}
                      </>
                    ) : (
                      <div
                        className="pdf-page-placeholder d-flex flex-column align-items-center justify-content-center bg-light text-muted"
                        style={{
                          width: `${pageCardWidth}px`,
                          height: `${pageCardHeight}px`,
                          userSelect: 'none',
                        }}
                      >
                        <div className="spinner-border spinner-border-sm text-secondary mb-2" role="status" />
                        <span className="small text-muted" style={{ fontSize: '0.75rem' }}>
                          Page {pageNum}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

PdfCanvasViewer.propTypes = {
  url: PropTypes.string,
  lineBlocks: PropTypes.arrayOf(PropTypes.object),
  activePage: PropTypes.number,
  activeLineNumber: PropTypes.number,
  activeSnippet: PropTypes.string,
  activeCategory: PropTypes.string,
  activeEntityField: PropTypes.string,
  activeEntityValue: PropTypes.string,
  fileName: PropTypes.string,
  entities: PropTypes.arrayOf(PropTypes.object),
  onSnippetClick: PropTypes.func,
  onShowPopover: PropTypes.func,
}
